import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import productService from '../../db/product-service';
import cartService from '../../db/cart-service';
import authService from '../../services/auth-service';
import { getProductContent } from '../../utils/productContentData';
import { countryCurrencyMap, currencySymbols } from '../../db/init-data';
import { formatPrice, detectUserCountry } from '../../utils/currency';
import './ProductDetails.scss';

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [userCountry, setUserCountry] = useState('US');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMedia, setSelectedMedia] = useState(0);

  useEffect(() => {
    const initCountry = async () => {
      const savedCountry = sessionStorage.getItem('userCountry');
      if (savedCountry) {
        setUserCountry(savedCountry);
      } else {
        const country = await detectUserCountry();
        setUserCountry(country);
        sessionStorage.setItem('userCountry', country);
      }
    };
    initCountry();
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const products = await productService.getAllProducts(true);
        const found = products.find(p => p._id === productId);
        setProduct(found);
      } catch (err) {
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };
    if (productId) fetchProduct();
  }, [productId]);

  const handleAddToCart = async () => {
    try {
      const userId = authService.getCurrentUserId();
      if (!userId) {
        showToast('Please log in to add items to cart', 'error');
        navigate('/login');
        return;
      }
      
      if (product) {
        const cart = await cartService.getCart(userId);
        const isPresent = cart.some(item => item.productId === product._id);
        
        await cartService.addToCart(userId, product._id, quantity, isPresent);
        showToast(`${product.name} added to cart!`);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast(error.message || 'Failed to add item to cart', 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 12px 20px;
      background-color: ${type === 'success' ? '#4caf50' : '#f44336'};
      color: white;
      border-radius: 4px;
      z-index: 9999;
      font-family: Arial, sans-serif;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 3000);
  };

  const allMedia = [
    { type: 'image', src: product?.image_url, label: 'Main Image' },
    ...(product?.galleryImages?.filter(img => img && img.trim()) || []).map((img, idx) => ({
      type: 'image',
      src: img,
      label: `Image ${idx + 2}`
    })),
    ...(product?.videoUrl && product.videoUrl.trim() ? [{ type: 'video', src: product.videoUrl.trim(), label: 'Video' }] : [])
  ].filter(m => m.src);

  if (loading) return <div className="loading">Loading product...</div>;
  if (!product) return <div className="error">Product not found</div>;

  const content = getProductContent(product.name) || {};
  const currencyCode = countryCurrencyMap[userCountry];
  const price = product.pricing[currencyCode];
  const currentMedia = allMedia[selectedMedia];

  return (
    <div className="product-details">
      <button 
        onClick={() => navigate('/')}
        style={{
          marginBottom: '12px',
          padding: '6px 12px',
          backgroundColor: '#2e7d32',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: '600'
        }}
      >
        ← Back to Products
      </button>
      <div className="product-details__container">
        {/* Image Gallery */}
        <div className="product-details__gallery">
          <div className="gallery__main">
            {allMedia.length > 0 && currentMedia?.type === 'image' ? (
              <img 
                src={currentMedia?.src}
                alt={product.name}
                className="product-details__main-image"
                style={{ width: '100%', aspectRatio: '16/9', objectFit: 'contain', background: '#f9f9f9' }}
                onError={(e) => {
                  console.warn('Image failed to load:', currentMedia?.src);
                  e.target.src = 'https://placehold.co/500x500?text=Image+Not+Found';
                }}
              />
            ) : allMedia.length > 0 && currentMedia?.type === 'video' ? (
              <iframe
                key={`video-${selectedMedia}`}
                width="100%"
                style={{ aspectRatio: '16/9' }}
                src={`${currentMedia?.src}?rel=0&modestbranding=1`}
                title="Product Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div style={{ width: '100%', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0' }}>
                <p>No media available</p>
              </div>
            )}
          </div>
          
          {allMedia.length > 1 && (
            <div className="gallery__thumbnails">
              {allMedia.map((media, idx) => (
                <button
                  key={idx}
                  className={`thumbnail ${selectedMedia === idx ? 'active' : ''}`}
                  onClick={() => setSelectedMedia(idx)}
                  title={media.label}
                >
                  {media.type === 'image' ? (
                    <img 
                      src={media.src} 
                      alt={media.label} 
                      onError={(e) => {
                        console.warn('Thumbnail failed to load:', media.src);
                        e.target.src = 'https://placehold.co/80x80?text=Error';
                      }} 
                    />
                  ) : (
                    <div className="thumbnail__play">▶ Video</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="product-details__info">
          <h1 className="product-details__title">{product.name}</h1>
          {product.description && (
            <p style={{fontSize: '14px', color: '#666', margin: '0', lineHeight: '1.6'}}>
              {product.description}
            </p>
          )}
          
          {product.benefits && (
            <div className="benefits-section">
              <h3>Health Benefits</h3>
              <ul>
                {product.benefits.split('\n').map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {(content?.appearance || product.appearance) && (
            <div className="appearance-section">
              <h3>Appearance &amp; Taste</h3>
              <ul>
                {(content?.appearance || product.appearance).split('. ').filter(s => s.trim()).map((item, idx, arr) => (
                  <li key={idx}>{item.trim()}{idx < arr.length - 1 ? '.' : ''}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Price */}
          <div className="product-details__price">
            {formatPrice(price, userCountry)} / {product.unit || 'unit'}
          </div>

          {/* Quantity & Add to Cart */}
          <div className="product-details__actions">
            <div className="quantity-selector">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
              <input type="number" value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} />
              <button onClick={() => setQuantity(quantity + 1)}>+</button>
            </div>
            <button className="btn-add-to-cart" onClick={handleAddToCart}>
              Add to Cart
            </button>
          </div>

          {/* Certifications */}
          {product.certifications && (
            <div className="product-details__certifications">
              <p className="certifications__label">Certifications:</p>
              <p className="certifications__text">{product.certifications}</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs and Subscription */}
      <div className="tabs-subscription-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', gridColumn: '1 / -1' }}>
        <div className="product-details__tabs">
          <div className="tabs__nav">
            <button 
              className={`tab__link ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
          </div>

          <div className="tabs__content">
            {activeTab === 'overview' && (
              <div className="tab__pane">
                <h2>Product Overview</h2>
                <p>{product.description}</p>
                
                {product.highlights && (
                  <div className="highlights">
                    <h3>Key Highlights</h3>
                    <ul>
                      {product.highlights.split('\n').map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="ingredients-section">
                  <h3>Ingredients</h3>
                  {product.ingredients ? (
                    <p>{product.ingredients}</p>
                  ) : (
                    <p>No ingredients information available</p>
                  )}
                </div>

                <div className="shelf-life-section">
                  <h3>Shelf Life</h3>
                  {product.shelfLife ? (
                    <p>{product.shelfLife}</p>
                  ) : (
                    <p>No shelf life information available</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Subscription Section */}
        <div className="subscription-section">
          <div className="subscription-badge">BEST VALUE</div>
          <div className="subscription-option">
            <label className="subscription-label">
              <input type="radio" name="purchaseOption" value="subscription" defaultChecked />
              <span className="subscription-text">Subscribe & Save 15%</span>
            </label>
            <div className="subscription-prices">
              <span className="original-price">₹{(price * 1.18).toFixed(2)}</span>
              <span className="discounted-price">₹{(price * 0.85).toFixed(2)}</span>
              <span className="savings-badge">Save 15%</span>
            </div>
            <p className="unit-price">₹{(price * 0.85).toFixed(2)} per {product.unit}</p>
          </div>
          <div className="subscription-benefits">
            <h4>✨ Subscription Benefits</h4>
            <ul>
              <li>Save 15% on every order</li>
              <li>Availability of shipping at your door step</li>
              <li>Reschedule to tomorrow, if you are not present today</li>
              <li>Flexible delivery time slots</li>
            </ul>
          </div>
          <button className="btn-subscribe" onClick={() => navigate('/subscriptions')}>
            Subscribe Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
